import React, { useEffect, useState } from 'react';
import { Alert, Button, Snackbar } from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import IosShareIcon from '@mui/icons-material/IosShare';
import { useTranslation } from 'react-i18next';

const DISMISS_KEY = 'pwa_install_dismissed_at';
const DISMISS_MS = 14 * 24 * 60 * 60 * 1000;

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const isStandalone = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  const iosStandalone = Boolean((window.navigator as Navigator & { standalone?: boolean }).standalone);
  return iosStandalone || window.matchMedia('(display-mode: standalone)').matches;
};

const isIosDevice = (): boolean => {
  if (typeof navigator === 'undefined') {
    return false;
  }
  const ua = navigator.userAgent;
  const iPhoneOrIpad = /iphone|ipad|ipod/i.test(ua);
  const iPadOs = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  return iPhoneOrIpad || iPadOs;
};

const isNarrowScreen = (): boolean =>
  typeof window !== 'undefined' && window.matchMedia('(max-width: 900px)').matches;

const wasDismissedRecently = (): boolean => {
  const raw = localStorage.getItem(DISMISS_KEY);
  if (!raw) {
    return false;
  }
  const dismissedAt = Number(raw);
  if (Number.isNaN(dismissedAt)) {
    return false;
  }
  return Date.now() - dismissedAt < DISMISS_MS;
};

const PwaInstallBanner: React.FC = () => {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showIosHint, setShowIosHint] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (isStandalone() || wasDismissedRecently()) {
      return undefined;
    }

    const onBeforeInstall = (event: Event) => {
      event.preventDefault();
      if (!isNarrowScreen()) {
        return;
      }
      setDeferredPrompt(event as BeforeInstallPromptEvent);
      setOpen(true);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    if (isIosDevice() && isNarrowScreen()) {
      setShowIosHint(true);
      setOpen(true);
    }

    const onInstalled = () => {
      setOpen(false);
      setDeferredPrompt(null);
    };
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setOpen(false);
  };

  const handleInstall = async () => {
    if (!deferredPrompt) {
      return;
    }
    await deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setOpen(false);
  };

  if (!open) {
    return null;
  }

  const canPrompt = Boolean(deferredPrompt);
  const title = canPrompt ? t('pwa.install_title') : t('pwa.ios_title');
  const message = canPrompt ? t('pwa.install_message') : t('pwa.ios_message');

  return (
    <Snackbar
      open={open}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      sx={{ bottom: { xs: 16, sm: 24 } }}
    >
      <Alert
        icon={canPrompt ? <GetAppIcon /> : <IosShareIcon />}
        severity="info"
        variant="outlined"
        onClose={handleDismiss}
        sx={{ alignItems: 'center', bgcolor: 'background.paper', width: '100%' }}
        action={
          canPrompt ? (
            <Button color="primary" size="small" onClick={handleInstall}>
              {t('pwa.install_button')}
            </Button>
          ) : showIosHint ? (
            <Button color="inherit" size="small" onClick={handleDismiss}>
              {t('pwa.later')}
            </Button>
          ) : undefined
        }
      >
        <strong>{title}</strong>
        {' — '}
        {message}
      </Alert>
    </Snackbar>
  );
};

export default PwaInstallBanner;
